import React, { useEffect, useMemo, useCallback } from 'react';
import { baseURL } from '@/lib/axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSecureItem } from '@/utils/localStorage';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as ExcelJS from "exceljs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Search,
  Mail,
  Calendar,
  Clock,
  FileText,
  UserCircle,
  Download,
  X,
  Phone,
  Edit,
  Activity,
  AlertTriangle,
  UserPlus,
  Linkedin,
  ExternalLink,
  Trash2,
  Briefcase,
  MessageSquare,
  History,
  MoreVertical,
  ChevronDown,
  Info,
  MapPin,
  Share2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/authStore";
import { useSharedData } from "@/hooks/useSharedData";
import { useCandidates } from '@/hooks/useCandidates';
import { candidateService } from '@/services/candidate.service';
import { Candidate, TimelineEntry, CreateCandidateRequest, HiringStatus } from '@/types/models';
import { AddCandidateDialog } from '@/components/hiring/AddCandidateDialog';
import CandidateTimelineDialog from '@/components/hiring/CandidateTimelineDialog';
import { getJobPostings, JobPosting } from '@/services/job.service';
import { interviewService } from '@/services/interview.service';
import { Interview } from '@/types/models';
import { format, parseISO, isValid } from 'date-fns';

interface CandidatesListProps {
  limit?: number;
  showFilters?: boolean;
}

// Helper function to check if candidate status is rejected/terminated
const isRejectedStatus = (status: any) => {
  const statusStr = String(status).toUpperCase();
  return statusStr === HiringStatus.REJECTED ||
    statusStr === HiringStatus.CANDIDATE_REJECTED ||
    statusStr === HiringStatus.OFFER_REJECTED;
};

// Helper function to check if candidate status is hired

// Next Actions Helper — clean pipeline using predefined journey

// Helper to format time strings (HH:mm:ss or ISO to 12h format)

const CandidatesList: React.FC<CandidatesListProps> = ({ limit, showFilters = true }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // const { toast } = useToast(); - Removed as we use sonner directly
  const { hasSubModulePermission, isLoading } = useAuthStore();
  const { employees } = useSharedData();

  // No need to fetch dynamic statuses

  // Check authentication
  useEffect(() => {
    // Only check authentication if not loading
    if (!isLoading) {
      const token = getSecureItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }
    }
  }, [navigate, isLoading]);

  // Check if user has basic candidate view permissions
  const canViewCandidates = hasSubModulePermission('hiring', 'candidate', 'view');
  const canCreateCandidates = hasSubModulePermission('hiring', 'candidate', 'create');
  const canEditCandidates = hasSubModulePermission('hiring', 'candidate', 'edit');
  const canDeleteCandidates = hasSubModulePermission('hiring', 'candidate', 'delete');

  // Check if user has interview permissions

  // Early return if no view permission
  if (!canViewCandidates) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">
            You don't have permission to view candidates. Contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // Get saved state for restoration
  const savedState = React.useMemo(() => {
    const saved = sessionStorage.getItem('candidatesList_scrollState');
    return saved ? JSON.parse(saved) : null;
  }, []);

  const [searchQuery, setSearchQuery] = React.useState(savedState?.search || '');
  const [statusFilter, setStatusFilter] = React.useState(savedState?.status || 'all');
  const [jobPostingFilter, setJobPostingFilter] = React.useState(savedState?.job || 'all');
  const [jobPostings, setJobPostings] = React.useState<JobPosting[]>([]);
  const [interviews, setInterviews] = React.useState<Interview[]>([]);
  const [interviewFilter, setInterviewFilter] = React.useState(savedState?.interview || 'all');
  const [locationFilter, setLocationFilter] = React.useState(savedState?.location || 'all');
  const [createdByFilter, setCreatedByFilter] = React.useState(savedState?.createdBy || 'all');
  const [creatorIds, setCreatorIds] = React.useState<string[]>([]);

  // Fetch all job postings for the filter dropdown
  useEffect(() => {
    getJobPostings()
      .then((res) => {
        const list: JobPosting[] = Array.isArray(res) ? res : res?.data ?? [];
        // Only show OPEN jobs in the filter
        const openJobs = list.filter(job => {
          const statusVal = typeof job.status === 'string'
            ? job.status
            : (job.status as any)?.value || (job.status as any)?.name || (job.status as any)?._id;
          return String(statusVal).toUpperCase() === 'OPEN';
        });
        setJobPostings(openJobs);
      })
      .catch(() => setJobPostings([]));
  }, []);

  // Fetch all interviews for filtering
  const fetchInterviews = React.useCallback(() => {
    interviewService.getInterviews({ limit: 1000 })
      .then(res => setInterviews(res.data || []))
      .catch(() => setInterviews([]));
  }, []);

  useEffect(() => {
    // Check for jobId in URL search params
    const jobId = searchParams.get('jobId');
    if (jobId) {
      setJobPostingFilter(jobId);
    }

    fetchInterviews();
  }, [fetchInterviews]);
  const [selectedResume, setSelectedResume] = React.useState<Candidate | null>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(savedState?.page || 1);
  const [pageSize, setPageSize] = React.useState<number>(savedState?.pageSize || limit || 10);
  const isFirstRender = React.useRef(true);

  // Edit dialog state
  const [editingCandidate, setEditingCandidate] = React.useState<Candidate | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);

  // Add dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);

  // Timeline states
  const [selectedTimelineCandidate, setSelectedTimelineCandidate] = React.useState<Candidate | null>(null);
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = React.useState(false);
  const [] = React.useState<{ event: TimelineEntry, candidate: Candidate } | null>(null);



  // Feedback states
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = React.useState(false);
  const [feedbackCandidate, setFeedbackCandidate] = React.useState<Candidate | null>(null);
  const [feedbackStatus, setFeedbackStatus] = React.useState<string>('');
  const [feedbackText, setFeedbackText] = React.useState('');
  const [expandedStatusGroup, setExpandedStatusGroup] = React.useState<string | null>(null);



  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = React.useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'info'
  });



  // Use our candidates hook with default filters
  const {
    candidates,
    total: serverTotal,
    loading,
    error,

    fetchCandidates,
    createCandidate,
    updateCandidate,
    updateCandidateStatus,
    deleteCandidate,
    clearError
  } = useCandidates();

  // Debounce search query to avoid too many API calls while typing
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Helper: Build filters object for the current UI filter state
  const buildRefreshFilters = React.useCallback((page: number): any => {
    const f: any = { page, limit: pageSize };
    if (statusFilter !== 'all') f.status = statusFilter;
    if (debouncedSearch) f.name = debouncedSearch;
    if (jobPostingFilter !== 'all') f.position = jobPostingFilter;
    if (locationFilter !== 'all') f.location = locationFilter;
    if (interviewFilter !== 'all') f.interviewFilter = interviewFilter;
    if (createdByFilter !== 'all') f.createdBy = createdByFilter;
    return f;
  }, [pageSize, statusFilter, debouncedSearch, jobPostingFilter, locationFilter, interviewFilter, createdByFilter]);

  // Trigger API fetch when any server-side filter changes
  useEffect(() => {
    fetchCandidates(buildRefreshFilters(currentPage));
  }, [currentPage, buildRefreshFilters, fetchCandidates]);

  // Fetch unique creator IDs to filter the dropdown
  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const ids = await candidateService.getCandidateCreators();
        setCreatorIds(ids);
      } catch (error) {
        console.error('Failed to fetch candidate creators:', error);
      }
    };
    fetchCreators();
  }, []);

  // Helper function to safely get department name
  const getDepartmentName = useCallback((department: Candidate['department']): string => {
    if (!department) return '';
    if (typeof department === 'string') return department;
    if (typeof department === 'object' && department.name) return department.name;
    return '';
  }, []);

  // Note on Filtering:
  // All major filters (Search, Status, Job Posting, Location, Interview Date) are handled server-side.

  // Reset to page 1 whenever server-side filters change, but skip on first render if we restored state
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, jobPostingFilter, locationFilter, interviewFilter, createdByFilter]);

  // Calculate pagination using server-side total
  const totalItems = serverTotal;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Ensure currentPage is within bounds
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Display the candidates returned from the current paginated server response.
  // All filters (Position, Location, Status, Search, Interview) are server-side now.
  const displayedCandidates = useMemo(() => {
    return Array.isArray(candidates) ? candidates : [];
  }, [candidates]);


  // Helper function to show confirmation dialog
  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmText?: string;
      cancelText?: string;
      type?: 'danger' | 'warning' | 'info';
    }
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      type: options?.type || 'info'
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    closeConfirmDialog();
  };



  const handleUpdateStatus = async (candidate: Candidate, newStatus: string) => {
    const statusStr = String(newStatus).toUpperCase();
    const statusName = candidateService.getStatusDisplayText(statusStr);

    if (!canEditCandidates) {
      toast.error("Permission Denied", {
        description: "You don't have permission to update candidate status",
      });
      return;
    }

    const currentStatusStr = String(candidate.status).toUpperCase();

    if (currentStatusStr === statusStr) {
      toast.info("No Change", {
        description: "Status is already set to this value",
      });
      return;
    }

    // No status change restriction - all statuses are reversible

    if (statusStr === HiringStatus.INTERVIEW_COMPLETED) {
      setFeedbackCandidate(candidate);
      setFeedbackStatus(statusStr);
      setFeedbackText('');
      setIsFeedbackDialogOpen(true);
      return;
    }

    if (isRejectedStatus(statusStr)) {
      showConfirmDialog(
        'Terminate Application',
        `Are you sure you want to terminate the application for ${candidateService.getCandidateFullName(candidate)}?`,
        () => updateStatusConfirmed(candidate, statusStr),
        {
          confirmText: 'Terminate Application',
          cancelText: 'Cancel',
          type: 'danger'
        }
      );
    } else {
      showConfirmDialog(
        'Update Status',
        `Are you sure you want to update ${candidateService.getCandidateFullName(candidate)}'s status to "${statusName}"?`,
        () => updateStatusConfirmed(candidate, statusStr),
        {
          confirmText: 'Update Status',
          cancelText: 'Cancel',
          type: 'info'
        }
      );
    }
  };

  const updateStatusConfirmed = async (candidate: Candidate, newStatus: string, notes?: string) => {
    try {
      // Convert status to string to handle both string and number values
      const statusStr = String(newStatus);

      const statusName = statusStr.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

      // Use provided notes or a default status update message
      const statusUpdateNotes = notes || `Status updated from candidate profile - ${candidateService.getStatusDisplayText(candidate.status)} to ${statusName}`;

      const isStatusChanging = String(candidate.status) !== statusStr;

      if (isStatusChanging) {
        // Make the API call first - this will handle adding the timeline entry
        await updateCandidateStatus(candidate._id, statusStr, statusUpdateNotes);
      } else {
        // Just editing feedback/notes without changing status
        // Call the updateCandidate method directly to avoid creating a new timeline note
        await updateCandidate(candidate._id, { feedback: notes || '' });
      }

      // Refresh current page after status update
      await fetchCandidates(buildRefreshFilters(currentPage));

      toast.success("Status Updated Successfully", {
        description: `${candidateService.getCandidateFullName(candidate)}'s status updated to ${statusName}`,
      });
    } catch (error: any) {
      // Handled globally
    }
  };

  const handleAddCandidate = async (newCandidateData: any) => {
    try {
      const candidateData: CreateCandidateRequest & { resumeFile?: File } = {
        firstName: newCandidateData.firstName,
        lastName: newCandidateData.lastName,
        email: newCandidateData.email,
        phone: newCandidateData.phone,
        currentCompany: newCandidateData.currentCompany,
        currentRole: newCandidateData.currentRole,
        experienceYears: newCandidateData.experienceYears || 0,
        expectedSalary: newCandidateData.expectedSalary,
        currentSalary: newCandidateData.currentSalary,
        noticePeriod: newCandidateData.noticePeriod,
        resumeLink: newCandidateData.resumeLink,
        department: newCandidateData.department || null,
        position: newCandidateData.position,
        linkedinUrl: newCandidateData.linkedinUrl,
        reasonForLeavingPreviousRole: newCandidateData.reasonForLeavingPreviousRole,
        communicationSkills: newCandidateData.communicationSkills,

        resumeFile: newCandidateData.resumeFile,
        source: newCandidateData.source,
        location: newCandidateData.location,
        notes: newCandidateData.notes,
      };

      await createCandidate(candidateData);

      // After add, go to page 1 to show the newest candidate
      setCurrentPage(1);
      await fetchCandidates(buildRefreshFilters(1));

      // Close the dialog
      setIsAddDialogOpen(false);

      toast.success("Candidate Added Successfully!", {
        description: `${candidateData.firstName} ${candidateData.lastName} has been added to the hiring pipeline`,
      });

    } catch (error: any) {
      // Handled globally
      throw error;
    }
  };

  const handleExportExcel = async () => {
    try {
      // For export, fetch ALL matching candidates from server (limit: 0)
      const exportFilters: any = buildRefreshFilters(1);
      exportFilters.limit = 0; // override to get all records
      delete exportFilters.page;
      const res = await candidateService.getCandidates(exportFilters);
      const dataToExport = res?.data || [];

      // Sort by Name
      dataToExport.sort((a, b) =>
        candidateService.getCandidateFullName(a).localeCompare(candidateService.getCandidateFullName(b))
      );

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Candidates");

      // Pre-calculate interviews for each candidate to find max rounds
      const candidatesWithInterviews = dataToExport.map(c => {
        const candidateId = c._id;
        const candidateInterviews = interviews.filter(i => {
          const id = (i.candidate && typeof i.candidate === 'object') ? (i.candidate as any)._id : i.candidate;
          return id === candidateId;
        }).sort((a, b) => {
          const dateA = a.interviewDate || '';
          const dateB = b.interviewDate || '';
          if (dateA !== dateB) return dateA.localeCompare(dateB);
          return (a.interviewTime || '').localeCompare(b.interviewTime || '');
        });
        return { candidate: c, interviews: candidateInterviews };
      });

      const maxRounds = Math.max(0, ...candidatesWithInterviews.map(ci => ci.interviews.length));

      // Define columns and headers
      const columns = [
        { header: "Full Name", key: "fullName", width: 25 },
        { header: "Email", key: "email", width: 30 },
        { header: "Phone", key: "phone", width: 15 },
        { header: "Status", key: "status", width: 20 },
        { header: "Position", key: "position", width: 20 },
        { header: "Department", key: "department", width: 20 },
        { header: "Location", key: "location", width: 20 },
        { header: "Experience", key: "experience", width: 12 },
        { header: "Current Salary", key: "currentSalary", width: 15 },
        { header: "Expected Salary", key: "expectedSalary", width: 15 },
        { header: "Notice Period", key: "noticePeriod", width: 15 },
        { header: "Applied Date", key: "appliedDate", width: 20 },
        { header: "LinkedIn", key: "linkedin", width: 40 },
      ];

      // Add dynamic round columns
      for (let i = 1; i <= maxRounds; i++) {
        columns.push({ header: `Round ${i}`, key: `round${i}`, width: 35 });
      }

      worksheet.columns = columns;

      // Style Header
      const headerRow = worksheet.getRow(1);
      headerRow.height = 30;
      headerRow.eachCell((cell: any) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4F46E5" },
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
          size: 12,
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      // Add data rows
      candidatesWithInterviews.forEach(({ candidate: c, interviews: candidateInterviews }) => {
        const deptName = getDepartmentName(c.department);
        const posName = (c as any).position
          ? (typeof (c as any).position === 'object'
            ? (c as any).position.title || (c as any).position.name
            : (c as any).position)
          : "N/A";

        const rowData: any = {
          fullName: candidateService.getCandidateFullName(c),
          email: c.email || "N/A",
          phone: c.phone || "N/A",
          status: candidateService.getStatusDisplayText(c.status),
          position: posName,
          department: deptName || "N/A",
          location: c.location || "N/A",
          experience: `${c.experienceYears || 0} Years`,
          currentSalary: c.currentSalary || 0,
          expectedSalary: c.expectedSalary || 0,
          noticePeriod: c.noticePeriod || "N/A",
          appliedDate: format(new Date(c.createdAt), "dd MMM, yyyy"),
          linkedin: c.linkedinUrl || "N/A",
        };

        // Fill in interview details for each round
        candidateInterviews.forEach((i, idx) => {
          const dateStr = i.interviewDate ? format(parseISO(i.interviewDate), "dd MMM yyyy") : 'TBD';
          const interviewers = Array.isArray(i.interviewers)
            ? i.interviewers.map(interviewer => getInterviewerName(interviewer)).join(', ')
            : 'N/A';
          rowData[`round${idx + 1}`] = `Type: ${i.interviewType || 'Interview'}\nDate: ${dateStr}\nTime: ${i.interviewTime || 'TBD'}\nPanel: ${interviewers}`;
        });

        const row = worksheet.addRow(rowData);
        row.alignment = { wrapText: true, vertical: 'middle' };
      });

      // Generate buffer and save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Candidates_Report_${format(new Date(), "ddMMyyyy")}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);

      toast.success("Export Successful", {
        description: "Candidate list has been exported to Excel.",
      });
    } catch (error: any) {
      // Handled globally
    }
  };

  const handleUpdateCandidate = async (candidateId: string, updatedCandidateData: any) => {
    try {
      const candidateData = {
        firstName: updatedCandidateData.firstName,
        lastName: updatedCandidateData.lastName,
        email: updatedCandidateData.email,
        phone: updatedCandidateData.phone,
        currentCompany: updatedCandidateData.currentCompany,
        currentRole: updatedCandidateData.currentRole,
        experienceYears: updatedCandidateData.experienceYears || 0,
        expectedSalary: updatedCandidateData.expectedSalary,
        currentSalary: updatedCandidateData.currentSalary,
        noticePeriod: updatedCandidateData.noticePeriod,
        resumeLink: updatedCandidateData.resumeLink,
        department: updatedCandidateData.department || null,
        position: updatedCandidateData.position,
        linkedinUrl: updatedCandidateData.linkedinUrl,
        reasonForLeavingPreviousRole: updatedCandidateData.reasonForLeavingPreviousRole,
        communicationSkills: updatedCandidateData.communicationSkills,

        resumeFile: updatedCandidateData.resumeFile,
        source: updatedCandidateData.source,
        location: updatedCandidateData.location,
        notes: updatedCandidateData.notes,
        // Note: resumeRemoved is not supported by the backend
        // resumeRemoved: updatedCandidateData.resumeRemoved,
      };

      await updateCandidate(candidateId, candidateData);

      // Refresh current page after update
      await fetchCandidates(buildRefreshFilters(currentPage));

      // Close edit dialog
      setIsEditDialogOpen(false);
      setEditingCandidate(null);

      toast.success("Candidate Updated Successfully", {
        description: `${candidateData.firstName} ${candidateData.lastName}'s information has been updated`,
      });
    } catch (error: any) {
      // Handled globally
      throw error;
    }
  };

  const handleEditCandidate = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    setIsEditDialogOpen(true);
  };

  const handleDeleteCandidate = async (candidate: Candidate) => {
    showConfirmDialog(
      'Delete Candidate',
      `Are you sure you want to delete ${candidateService.getCandidateFullName(candidate)}?\n\nThis action cannot be undone.`,
      async () => {
        try {
          await deleteCandidate(candidate._id);

          // After delete, stay on current page
          await fetchCandidates(buildRefreshFilters(currentPage));

          // Close résumé dialog if the deleted candidate was selected
          if (selectedResume && selectedResume._id === candidate._id) {
            setSelectedResume(null);
          }

          toast.success("Candidate Deleted Successfully", {
            description: `${candidateService.getCandidateFullName(candidate)} has been removed from the system`,
          });
        } catch (error: any) {
          // Handled globally
        }
      },
      {
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger'
      }
    );
  };


  const handleViewProfile = (candidate: Candidate) => {
    // Save current state and candidate ID for return
    sessionStorage.setItem('candidatesList_lastCandidateId', candidate._id);
    sessionStorage.setItem('candidatesList_scrollState', JSON.stringify({
      page: currentPage,
      pageSize,
      search: searchQuery,
      status: statusFilter,
      job: jobPostingFilter,
      location: locationFilter,
      interview: interviewFilter,
      createdBy: createdByFilter
    }));
    navigate(`/hiring/candidates/${candidate._id}`);
  };

  const handleViewResume = (candidate: Candidate) => {
    setSelectedResume(candidate);
    if (!candidate.resumeLink) {
      toast.error("No Resume Available", {
        description: `${candidateService.getCandidateFullName(candidate)} hasn't uploaded a resume yet`,
      });
    }
  };

  // Helper function to sanitize filename for download

  const handleDownloadResume = async (candidate: Candidate) => {
    if (candidate.resumeLink) {
      // Always use backend URL for download
      const backendFileName = getResumeHash(candidate.resumeLink);
      const completeBackendUrl = getCompleteBackendUrl(candidate.resumeLink);

      // Show download started toast
      toast.info("Download Started", {
        description: `Downloading: ${candidate.resumeFileName || backendFileName}`,
      });

      try {
        // Method 1: Direct download using window.open (most reliable)

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = completeBackendUrl;
        link.download = candidate.resumeFileName || backendFileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Add authorization header if token exists
        const token = getSecureItem('accessToken');
        if (token) {
          // For direct download, we'll use a different approach
          // Try fetch first, then fallback to direct link
          try {
            const response = await fetch(completeBackendUrl, {
              method: 'GET',
              headers: {
                'Accept': 'application/pdf,application/octet-stream,*/*',
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            // Create download link
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = candidate.resumeFileName || backendFileName;
            downloadLink.style.display = 'none';

            // Trigger download
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            // Clean up
            window.URL.revokeObjectURL(blobUrl);

            toast.success("Download Started", {
              description: `Downloading: ${candidate.resumeFileName || backendFileName}`,
            });
          } catch (fetchError) {
            // Fallback to direct link
            link.click();

            toast.info("Download Link Opened", {
              description: "If download doesn't start, right-click the link and 'Save as'",
            });
          }
        } else {
          // No token, use direct link
          link.click();

          toast.info("Download Link Opened", {
            description: "If download doesn't start, right-click the link and 'Save as'",
          });
        }
      } catch (error) {
        console.error('Download failed:', error);

        // Final fallback - open in new tab
        window.open(completeBackendUrl, '_blank');
        toast.info("Resume Opened in New Tab", {
          description: "Right-click and 'Save as' to download the file",
        });
      }
    } else {
      toast.error("No Resume Available", {
        description: `${candidateService.getCandidateFullName(candidate)} hasn't uploaded a resume yet`,
      });
    }
  };




  // Handler for department filter change
  const handleViewTimeline = (candidate: Candidate) => {
    setSelectedTimelineCandidate(candidate);
    setIsTimelineDialogOpen(true);
  };

  const handleTimelineUpdate = (timeline: TimelineEntry[]) => {
    // Update the candidate's timeline in the local state
    if (selectedTimelineCandidate) {
      const updatedCandidate = { ...selectedTimelineCandidate, timeline };
      setSelectedTimelineCandidate(updatedCandidate);

      // Also update in the main candidates list
      // We would need to update the candidates state here, but since we're using a hook,
      // we'll let the next fetch handle the update
    }
  };

  // Show error toast when error occurs
  useEffect(() => {
    if (error) {
      toast.error("Error Occurred", {
        description: error || 'Please refresh the page or try again later',
      });
      clearError();
    }
  }, [error, clearError]);

  // Scroll to last viewed candidate
  React.useEffect(() => {
    // Ensure data is loaded and rendered
    if (!loading && candidates.length > 0) {
      const lastCandidateId = sessionStorage.getItem('candidatesList_lastCandidateId');
      if (lastCandidateId) {
        // Reduced delay since we know candidates are loaded
        const timer = setTimeout(() => {
          const element = document.getElementById(`candidate-${lastCandidateId}`);
          if (element) {
            // Use block: 'center' to ensure the record is visible even if it was at the bottom/top
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a subtle highlight to help the user identify the record
            element.classList.add('bg-blue-50/80', 'transition-colors', 'duration-500');
            
            // Remove highlight and clean up session storage after a while
            setTimeout(() => {
              element.classList.remove('bg-blue-50/80');
              sessionStorage.removeItem('candidatesList_lastCandidateId');
            }, 3000);
          }
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, candidates]);



  // Helper to get interviewer name from ID or Object
  const getInterviewerName = (item: any) => {
    if (!item) return null;

    // 1. If it's an object with name information directly
    if (typeof item === 'object') {
      if (item.user && item.user.firstName) return `${item.user.firstName} ${item.user.lastName || ''}`.trim();
      if (item.firstName) return `${item.firstName} ${item.lastName || ''}`.trim();
      if (item.name) return item.name;
      if (item.email) return item.email.split('@')[0];
    }

    // 2. If it's an ID or an object with an ID, check local employees cache
    const targetId = typeof item === 'object' ? (item._id || item.id) : item;
    const emp = employees.find(e => (e._id === targetId || e.id === targetId));

    if (emp) {
      if (emp.user && emp.user.firstName) return `${emp.user.firstName} ${emp.user.lastName || ''}`.trim();
      if (emp.firstName) return `${emp.firstName} ${emp.lastName || ''}`.trim();
      return emp.name || emp.email?.split('@')[0] || 'Team Member';
    }

    return 'Assigned';
  };

  // Helper to get candidate initials (first letter of first and last name)
  const getCandidateInitials = (candidate: any) => {
    if (!candidate) return 'N/A';
    const first = candidate.firstName ? candidate.firstName[0] : '';
    const last = candidate.lastName ? candidate.lastName[0] : '';
    return (first + last).toUpperCase() || 'N/A';
  };

  // Function to extract hash from resume URL
  const getResumeHash = (resumeLink: string) => {
    if (!resumeLink) return 'hash.pdf';
    const parts = resumeLink.split('/');
    return parts[parts.length - 1] || 'hash.pdf';
  };

  // Function to get complete backend URL
  const getCompleteBackendUrl = (resumeLink: string) => {
    if (!resumeLink) return '';
    const baseUrl = baseURL.replace('/api/v1', '');
    const hash = getResumeHash(resumeLink);
    return `${baseUrl}/uploads/${hash}`;
  };







  return (
    <TooltipProvider>
      <div className="space-y-4 p-4 pt-1 pb-10">

        {showFilters && (
          <>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Candidates</h2>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {canCreateCandidates && (
                  <Button
                    onClick={() => {
                      setIsAddDialogOpen(true);
                    }}
                    size="default"
                    className="hover:bg-primary h-10 px-4"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Candidate
                  </Button>
                )}
              </div>
            </div>

            <Card className="border border-gray-200 shadow-none rounded-xl mb-4 bg-white overflow-hidden">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full">
                  <div className="relative group flex-1 min-w-[200px]">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <Input
                      placeholder="Search candidates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 rounded-lg border-gray-200 focus:border-blue-500 focus-visible:ring-0 focus-visible:ring-offset-0 text-xs bg-white"
                    />
                    {searchQuery && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-8 w-8 hover:bg-gray-100 rounded-lg"
                        onClick={() => setSearchQuery('')}
                      >
                        <X className="h-4 w-4 text-gray-400" />
                      </Button>
                    )}
                  </div>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-10 w-[130px] flex-shrink-0 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-xs bg-white">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <Activity className="h-4 w-4 text-gray-400 shrink-0" />
                        <SelectValue placeholder="Status" className="truncate" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-gray-200">
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.values(HiringStatus).map((status) => (
                        <SelectItem key={status} value={status}>
                          {candidateService.getStatusDisplayText(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>


                  <Select value={jobPostingFilter} onValueChange={setJobPostingFilter}>
                    <SelectTrigger className="h-10 w-[170px] flex-shrink-0 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-xs bg-white">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <Briefcase className="h-4 w-4 text-gray-400 shrink-0" />
                        <SelectValue placeholder="Job Posting" className="truncate" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-gray-200">
                      <SelectItem value="all">All Job Postings</SelectItem>
                      {jobPostings.map((job: JobPosting) => (
                        <SelectItem key={job._id} value={job._id!}>
                          {job.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger className="h-10 w-[130px] flex-shrink-0 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-xs bg-white">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
                        <SelectValue placeholder="Location" className="truncate" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-gray-200">
                      <SelectItem value="all">All Locations</SelectItem>
                      <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
                      <SelectItem value="Surat">Surat</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={interviewFilter} onValueChange={setInterviewFilter}>
                    <SelectTrigger className="h-10 w-[140px] flex-shrink-0 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-xs bg-white">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <History className="h-4 w-4 text-gray-400 shrink-0" />
                        <SelectValue placeholder="Interview" className="truncate" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-gray-200">
                      <SelectItem value="all">All Interviews</SelectItem>
                      <SelectItem value="today">Today's Interviews</SelectItem>
                      <SelectItem value="tomorrow">Tomorrow's Interviews</SelectItem>
                      <SelectItem value="upcoming">Upcoming Interviews</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                    <SelectTrigger className="h-10 w-[130px] flex-shrink-0 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-xs bg-white">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <UserCircle className="h-4 w-4 text-gray-400 shrink-0" />
                        <SelectValue placeholder="Added By" className="truncate" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-gray-200">
                      <SelectItem value="all">All Adders</SelectItem>
                      {employees
                        .filter(emp => {
                          const userId = emp.user?._id || emp._id;
                          return creatorIds.includes(userId);
                        })
                        .map((emp) => (
                          <SelectItem key={emp._id} value={emp.user?._id || emp._id}>
                            {getInterviewerName(emp)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  <div className="flex gap-2">


                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 shrink-0 rounded-lg border-gray-200 bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-none"
                            onClick={() => {
                              setSearchQuery("");
                              setStatusFilter("all");
                              setJobPostingFilter("all");
                              setInterviewFilter("all");
                              setLocationFilter("all");
                              setCreatedByFilter("all");
                              sessionStorage.removeItem('candidatesList_scrollState');
                              sessionStorage.removeItem('candidatesList_lastCandidateId');
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Clear Filters</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 shrink-0 rounded-lg border-gray-200 bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-none"
                            onClick={handleExportExcel}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Export to Excel</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {loading && displayedCandidates.length === 0 ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Candidates...</h2>
              <p className="text-gray-600">Please wait while we fetch your candidate data.</p>
            </div>
          </div>
        ) : displayedCandidates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {candidates.length === 0
                ? "No candidates found"
                : `No candidates match your current filters (${candidates.length} total candidates available)`
              }
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80 border-b border-gray-200">
                  <TableHead className="py-4 px-4 text-xs font-semibold text-gray-700">Name</TableHead>
                  <TableHead className="py-4 text-xs font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="py-4 text-xs font-semibold text-gray-700">Position & Exp</TableHead>
                  <TableHead className="py-4 text-xs font-semibold text-gray-700">Current & Expected</TableHead>
                  <TableHead className="py-4 text-xs font-semibold text-gray-700">Contact</TableHead>
                  <TableHead className="py-4 text-xs font-semibold text-gray-700">Availability</TableHead>
                  <TableHead className="py-4 text-xs font-semibold text-gray-700 text-center">Links & Docs</TableHead>
                  <TableHead className="py-4 px-4 text-right text-xs font-semibold text-gray-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedCandidates.map((candidate) => (
                  <TableRow key={candidate._id} id={`candidate-${candidate._id}`} className={`border-b border-gray-100 ${isRejectedStatus(candidate.status) ? 'opacity-70 bg-gray-50/30' : ''}`}>
                    <TableCell className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-xs shrink-0">
                          {getCandidateInitials(candidate)}
                        </div>
                        <div className="min-w-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="font-medium text-blue-600 text-sm hover:underline cursor-pointer truncate max-w-[100px]" onClick={() => handleViewProfile(candidate)}>
                                {candidateService.getCandidateFullName(candidate)}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{candidateService.getCandidateFullName(candidate)}</p>
                            </TooltipContent>
                          </Tooltip>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {new Date(candidate.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="h-6 w-fit min-w-[90px] bg-transparent border-none p-0 focus:outline-none hover:opacity-80 transition-opacity">
                            <Badge
                              variant="outline"
                              className="text-[11px] font-bold px-2 py-0.5 rounded border flex items-center gap-1 w-full justify-between cursor-pointer"
                              style={{
                                backgroundColor: candidateService.getStatusColor(String(candidate.status)) + '10',
                                color: candidateService.getStatusColor(String(candidate.status)),
                                borderColor: candidateService.getStatusColor(String(candidate.status)) + '30',
                              }}
                            >
                              <span className="truncate">{candidateService.getStatusDisplayText(String(candidate.status))}</span>
                              <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
                            </Badge>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="rounded-lg border-gray-100 shadow-xl min-w-[180px] p-1 overflow-y-auto max-h-[400px]">
                          {[
                            { label: 'Screening', statuses: [HiringStatus.SELECTED_FOR_INTERVIEW, HiringStatus.REJECTED] },
                            { label: 'Interviews', statuses: [HiringStatus.INTERVIEW_SCHEDULED, HiringStatus.INTERVIEW_COMPLETED, HiringStatus.SHORTLISTED] },
                            { label: 'Selection', statuses: [HiringStatus.CANDIDATE_SELECTED, HiringStatus.CANDIDATE_REJECTED] },
                            { label: 'Pipeline Management', statuses: [HiringStatus.FUTURE_REFERENCE, HiringStatus.ON_HOLD] },
                            { label: 'Offer & Hiring', statuses: [HiringStatus.OFFER_SENT, HiringStatus.OFFER_ACCEPTED, HiringStatus.OFFER_REJECTED, HiringStatus.HIRED] }
                          ].map((group) => (
                            <div key={group.label} className="mb-1 last:mb-0">
                              <button
                                className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-bold text-indigo-400 uppercase hover:bg-indigo-50/50 rounded transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedStatusGroup(expandedStatusGroup === group.label ? null : group.label);
                                }}
                              >
                                {group.label}
                                <ChevronDown className={`h-3 w-3 transition-transform ${expandedStatusGroup === group.label ? 'rotate-180' : ''}`} />
                              </button>
                              {expandedStatusGroup === group.label && (
                                <div className="mt-0.5 space-y-0.5 pl-1">
                                  {group.statuses.map((status) => {
                                    if (status === 'NEW') return null;
                                    const isCurrentStatus = String(candidate.status) === status;
                                    return (
                                      <DropdownMenuItem
                                        key={status}
                                        className={`text-[11px] py-1.5 px-2 rounded cursor-pointer ${isCurrentStatus ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 focus:bg-gray-50'}`}
                                        disabled={isCurrentStatus}
                                        onClick={() => handleUpdateStatus(candidate, status)}
                                      >
                                        {candidateService.getStatusDisplayText(status)}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="min-w-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-sm text-gray-700 truncate max-w-[120px]">
                              {((candidate as any).position
                                ? (typeof (candidate as any).position === 'object'
                                  ? (candidate as any).position.title || (candidate as any).position.name || (candidate as any).position.role || "General Recruitment"
                                  : (candidate as any).position)
                                : "General Recruitment")}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {((candidate as any).position
                                ? (typeof (candidate as any).position === 'object'
                                  ? (candidate as any).position.title || (candidate as any).position.name || (candidate as any).position.role || "General Recruitment"
                                  : (candidate as any).position)
                                : "General Recruitment")}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {candidate.experienceYears} Years Exp
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="text-xs space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-400 w-8">Curr:</span>
                          <span className="text-gray-700 font-medium">₹{candidate.currentSalary?.toLocaleString() || '-'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-400 w-8">Exp:</span>
                          <span className="text-indigo-600 font-medium">₹{candidate.expectedSalary?.toLocaleString() || '-'}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="space-y-1">
                        {candidate.email && (
                          <div className="flex items-center gap-1.5 text-gray-500">
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            <span className="text-xs truncate max-w-[130px]">{candidate.email}</span>
                          </div>
                        )}
                        {candidate.phone && (
                          <div className="flex items-center gap-1.5 text-gray-500">
                            <Phone className="h-3.5 w-3.5 shrink-0" />
                            <span className="text-xs">{candidate.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      {(() => {
                        // 1. If interview filter is active, show the scheduled rounds
                        if (interviewFilter !== 'all') {
                          const candidateInterviews = interviews.filter(i => {
                            const candidateId = (i.candidate && typeof i.candidate === 'object') ? (i.candidate as any)._id : i.candidate;
                            return candidateId === candidate._id;
                          }).sort((a, b) => {
                            const dateA = a.interviewDate || '';
                            const dateB = b.interviewDate || '';
                            if (dateA !== dateB) return dateA.localeCompare(dateB);
                            return (a.interviewTime || '').localeCompare(b.interviewTime || '');
                          });

                          if (candidateInterviews.length > 0) {
                            return (
                              <div className="flex flex-col gap-1.5">
                                {candidateInterviews.slice(0, 2).map((i) => (
                                  <div key={i._id} className="flex flex-col border-l-2 border-blue-400 pl-2 py-0.5">
                                    <div className="flex items-center gap-1.5 text-blue-700">
                                      <span className="text-[10px] font-bold uppercase tracking-wider">
                                        {i.interviewType || 'Interview'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-700">
                                      <Calendar className="h-3 w-3 text-gray-400" />
                                      <span className="text-[11px] font-medium">
                                        {(() => {
                                          const date = parseISO(i.interviewDate);
                                          return isValid(date) ? format(date, "dd MMM") : i.interviewDate;
                                        })()}
                                      </span>
                                      <span className="text-gray-300 mx-0.5">|</span>
                                      <Clock className="h-3 w-3 text-gray-400" />
                                      <span className="text-[11px] font-medium">{i.interviewTime}</span>
                                    </div>
                                  </div>
                                ))}
                                {candidateInterviews.length > 2 && (
                                  <span className="text-[10px] text-blue-500 font-semibold pl-2">
                                    +{candidateInterviews.length - 2} more rounds
                                  </span>
                                )}
                              </div>
                            );
                          }
                        }

                        // 2. Default or Fallback: show the original simple availability
                        return (
                          <span className="text-gray-300 text-xs font-medium">Not Scheduled</span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewResume(candidate)}
                          className={`h-8 w-8 p-0 rounded-lg transition-colors ${candidate.resumeLink ? 'text-green-500 hover:bg-green-50' : 'text-gray-200 cursor-not-allowed'}`}
                          disabled={!candidate.resumeLink}
                          title={candidate.resumeLink ? "View Resume" : "No Resume"}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>

                        {candidate.linkedinUrl ? (
                          <a
                            href={candidate.linkedinUrl.startsWith('http') ? candidate.linkedinUrl : `https://${candidate.linkedinUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-blue-50 text-blue-500 transition-colors"
                            title="LinkedIn Profile"
                          >
                            <Linkedin className="h-4 w-4" />
                          </a>
                        ) : (
                          <div className="h-8 w-8 flex items-center justify-center text-gray-200" title="No LinkedIn">
                            <Linkedin className="h-4 w-4 opacity-30" />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-right px-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewProfile(candidate)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Profile"
                        >
                          <UserCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewTimeline(candidate)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Journey"
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-lg border-gray-200">
                            <DropdownMenuItem className="text-[12px] py-2" onClick={() => {
                              const profileUrl = `${window.location.origin}/hiring/candidates/${candidate._id}`;
                              navigator.clipboard.writeText(profileUrl);
                              toast.success('Profile link copied!');
                            }}>
                              <Share2 className="h-3.5 w-3.5 mr-2 text-blue-500" />
                              Share Profile
                            </DropdownMenuItem>
                            {canEditCandidates && (
                              <DropdownMenuItem className="text-[12px] py-2" onClick={() => handleEditCandidate(candidate)}>
                                <Edit className="h-3.5 w-3.5 mr-2 text-indigo-500" />
                                Edit Info
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {canDeleteCandidates && (
                              <DropdownMenuItem className="text-[12px] py-2 text-red-600 hover:bg-red-50" onClick={() => handleDeleteCandidate(candidate)}>
                                <Trash2 className="h-3.5 w-3.5 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="flex items-center justify-between px-4 py-4 bg-white border-t border-gray-100">
                <div className="text-[12px] text-gray-500">
                  Showing <span className="font-medium text-gray-900">{(currentPage - 1) * pageSize + 1}</span> to{" "}
                  <span className="font-medium text-gray-900">{Math.min(currentPage * pageSize, totalItems)}</span> of{" "}
                  <span className="font-medium text-gray-900">{totalItems}</span> candidates
                </div>

                <div className="flex items-center gap-2">
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(v) => {
                      setPageSize(Number(v));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[100px] text-[11px] rounded-lg border-gray-100">
                      <SelectValue placeholder="Page Size" />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-gray-100">
                      {[10, 20, 50, 100].map(size => (
                        <SelectItem key={size} value={size.toString()} className="text-[11px]">
                          {size} per page
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev: number) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="h-8 px-3 rounded-lg border-gray-100 text-[11px] font-medium disabled:opacity-30"
                    >
                      Previous
                    </Button>

                    <div className="h-8 px-3 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-100 text-[11px] font-bold text-gray-900">
                      {currentPage}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev: number) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 px-3 rounded-lg border-gray-100 text-[11px] font-medium disabled:opacity-30"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}



        <Dialog open={!!selectedResume} onOpenChange={() => setSelectedResume(null)}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col">
            <DialogTitle className="sr-only">Resume Viewer</DialogTitle>
            <DialogDescription className="sr-only">Document viewer for candidate resumes.</DialogDescription>
            <DialogHeader className="border-b pb-4 flex-shrink-0">
              <DialogTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Resume - {selectedResume && candidateService.getCandidateFullName(selectedResume)}
                </div>
                {selectedResume && selectedResume.resumeLink && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadResume(selectedResume)}
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = getCompleteBackendUrl(selectedResume.resumeLink || '');
                        window.open(url, '_blank');
                        toast.info("Resume Opened", {
                          description: "Resume opened in new tab. Right-click and 'Save as' to download.",
                        });
                      }}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in Tab
                    </Button>
                  </div>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-6">
              {selectedResume && (
                <div className="space-y-6">
                  {selectedResume.resumeLink ? (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-blue-900">Resume Available</h3>
                            <p className="text-sm text-blue-700">Click the link below to view or download</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-blue-200">
                          <a
                            href={getCompleteBackendUrl(selectedResume.resumeLink || '')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline break-all text-sm font-medium cursor-pointer block bg-gray-50 p-2 rounded border"
                          >
                            {selectedResume.resumeFileName || 'resume.pdf'}
                          </a>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Resume Available</h3>
                      <p className="text-gray-600">
                        No resume link was provided for this candidate.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>





        <AddCandidateDialog
          isEditMode={true}
          candidate={editingCandidate}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onUpdateCandidate={handleUpdateCandidate}
        />


        <AddCandidateDialog
          isEditMode={false}
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onAddCandidate={handleAddCandidate}
        />


        <CandidateTimelineDialog
          candidate={selectedTimelineCandidate}
          open={isTimelineDialogOpen}
          onOpenChange={setIsTimelineDialogOpen}
          onTimelineUpdate={handleTimelineUpdate}
        />






        {/* Feedback Dialog */}
        <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
          <DialogContent className="sm:max-w-[480px] border-none shadow-2xl p-0 overflow-hidden rounded-2xl bg-white">
            <div className="bg-indigo-50/50 p-6 border-b border-indigo-100/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-white rounded-xl shadow-sm border border-indigo-100 flex items-center justify-center">
                  <MessageSquare className="h-4.5 w-4.5 text-indigo-600" />
                </div>
                <DialogTitle className="text-lg font-bold text-gray-900 tracking-tight">Interview Assessment</DialogTitle>
              </div>
              <DialogDescription className="text-gray-500 text-xs font-medium leading-relaxed">
                Share your professional findings for <span className="text-indigo-600 font-bold">{feedbackCandidate ? candidateService.getCandidateFullName(feedbackCandidate) : 'the candidate'}</span> to finalize this round.
              </DialogDescription>
            </div>

            <div className="p-6 space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="feedback" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em]">
                    Evaluation Notes
                  </label>
                  {!feedbackText.trim() && (
                    <span className="text-[9px] text-amber-500 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-100/50 uppercase">Required</span>
                  )}
                </div>
                <textarea
                  id="feedback"
                  className="w-full min-h-[160px] p-4 text-sm border border-gray-100 rounded-xl focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 transition-all outline-none resize-none bg-gray-50/50 placeholder:text-gray-300 font-medium"
                  placeholder="Describe candidate strengths, technical gaps, and your final recommendation..."
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsFeedbackDialogOpen(false)}
                  className="h-10 px-5 text-gray-400 hover:text-gray-600 text-xs font-bold uppercase tracking-wider"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!feedbackText.trim()}
                  onClick={() => {
                    if (feedbackCandidate && feedbackStatus) {
                      updateStatusConfirmed(feedbackCandidate, feedbackStatus, feedbackText);
                      setIsFeedbackDialogOpen(false);
                    }
                  }}
                  className="h-10 px-6 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold uppercase tracking-widest rounded-xl transition-all shadow-md shadow-indigo-200 disabled:opacity-50 disabled:shadow-none"
                >
                  Submit Assessment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={confirmDialog.open} onOpenChange={closeConfirmDialog}>
          <DialogContent className="sm:max-w-[440px] border border-gray-100 shadow-2xl p-0 overflow-hidden rounded-2xl bg-white">
            <DialogTitle className="sr-only">{confirmDialog.title}</DialogTitle>
            <DialogDescription className="sr-only">{confirmDialog.message}</DialogDescription>

            <div className="p-6">
              <div className="flex items-start gap-4 pt-2">
                {/* ── Minimal Professional Icon ── */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm border ${confirmDialog.type === 'danger' ? 'bg-red-50 border-red-100' :
                  confirmDialog.type === 'warning' ? 'bg-amber-50 border-amber-100' :
                    'bg-blue-50 border-blue-100'
                  }`}>
                  {confirmDialog.type === 'danger' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                  {confirmDialog.type === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                  {confirmDialog.type === 'info' && <Info className="h-5 w-5 text-blue-600" />}
                </div>

                {/* ── Content ── */}
                <div className="flex-1 min-w-0 pr-4">
                  <h3 className="text-[17px] font-bold text-gray-900 tracking-tight leading-tight mb-2">
                    {confirmDialog.title}
                  </h3>
                  <p className="text-[13.5px] text-gray-500 leading-relaxed font-medium">
                    {confirmDialog.message}
                  </p>
                </div>
              </div>

              {/* ── Professional Actions ── */}
              <div className="flex items-center justify-end gap-2.5 mt-8 pt-2">
                <Button
                  variant="ghost"
                  onClick={closeConfirmDialog}
                  className="h-9 px-4 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 text-[11px] font-bold uppercase tracking-wider transition-all"
                >
                  {confirmDialog.cancelText}
                </Button>
                <Button
                  variant={confirmDialog.type === 'danger' ? 'destructive' : 'default'}
                  onClick={handleConfirmAction}
                  className={`h-9 px-5 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm ${confirmDialog.type === 'danger' ? 'bg-red-500 hover:bg-red-600' :
                    confirmDialog.type === 'warning' ? 'bg-amber-500 hover:bg-amber-600' :
                      'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                >
                  {confirmDialog.confirmText}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div >
    </TooltipProvider >
  );
};

export default CandidatesList;